/**
 * The post-tasting wrap-up (route `/tastings/:id/wrapup`): "here's the tasting,
 * all the wines, the group scores, and here's your score". The host writes a
 * little blurb about how it went, picks a few photos from the night, chooses who
 * it goes to, checks it as any member would see it, sends a test to themselves,
 * then sends it — one personalised email per recipient.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MAX_WRAPUP_PHOTOS,
  WRAPUP_AUDIENCES,
  WRAPUP_AUDIENCE_LABELS,
  type ContributionPersonRef,
  type WrapupAudience,
  type WrapupDraftView,
} from '@gwc/shared';
import { wrapupApi } from '../api/wrapup';
import { tastingsApi } from '../api/tastings';
import { useAuth } from '../auth/AuthProvider';
import { ApiError } from '../lib/api';
import { eventDate, ordinal, points } from '../lib/format';
import { toast } from '../lib/toast';
import { Badge, Button, Textarea, useConfirm } from '../components/ui';
import { PersonPicker } from '../components/PersonPicker';
import { TestEventBadge } from '../components/TestEventBadge';

const AUDIENCE_HINT: Record<WrapupAudience, string> = {
  attended: 'Everyone marked present on the night.',
  all_members:
    "Every member, even the ones who weren't there. Anyone who didn't score gets the group's results only.",
};

export function TastingWrapup() {
  const { id = '' } = useParams<{ id: string }>();
  const event = useQuery({ queryKey: ['event', id], queryFn: () => tastingsApi.get(id) });
  // Only a final tasting has a wrap-up; don't ask the server for one otherwise.
  const notFinal = !!event.data && event.data.event.phase !== 'final';
  const draft = useQuery({
    queryKey: ['wrapup', id],
    queryFn: () => wrapupApi.draft(id),
    enabled: !!event.data && !notFinal,
    retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2,
  });

  const title = draft.data?.title ?? event.data?.event.title;
  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <Link to={`/tastings/${id}`} className="text-xs text-cream-300/60 hover:text-gold-200">
        ← Back to the tasting
      </Link>
      <header>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-display text-3xl text-cream-50">Wrap-up email</h1>
          <TestEventBadge isTest={event.data?.event.isTest} />
        </div>
        <p className="mt-1 text-sm text-cream-300/70">
          {title}
          {event.data ? ` · ${eventDate(event.data.event)}` : ''}
        </p>
      </header>

      {event.isLoading || draft.isLoading ? (
        <p className="text-sm text-cream-300/60">Loading…</p>
      ) : notFinal || draft.error || event.error ? (
        <div className="card p-6 text-sm text-cream-200">
          {notFinal || (draft.error instanceof ApiError && draft.error.code === 'not_final') ? (
            <>
              <p>The wrap-up opens once the tasting is finalized and its results are in.</p>
              <p className="mt-1 text-cream-300/60">
                If the tasting has been reopened for corrections, finalize it again first.
              </p>
            </>
          ) : (
            <p>
              {(draft.error ?? event.error) instanceof Error
                ? (draft.error ?? event.error)!.message
                : 'Could not load the wrap-up.'}
            </p>
          )}
        </div>
      ) : draft.data ? (
        <WrapupEditor eventId={id} view={draft.data} isTest={!!event.data?.event.isTest} />
      ) : null}
    </div>
  );
}

function WrapupEditor({
  eventId,
  view,
  isTest,
}: {
  eventId: string;
  view: WrapupDraftView;
  isTest: boolean;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { user } = useAuth();
  const [blurb, setBlurb] = useState(view.blurb ?? '');
  const [mediaIds, setMediaIds] = useState<string[]>(view.mediaIds);
  const [audience, setAudience] = useState<WrapupAudience>(view.audience);
  const [previewAs, setPreviewAs] = useState<ContributionPersonRef | null>(null);

  // Re-seed when a save comes back from the server.
  useEffect(() => {
    setBlurb(view.blurb ?? '');
    setMediaIds(view.mediaIds);
    setAudience(view.audience);
  }, [view.blurb, view.mediaIds, view.audience]);

  const dirty =
    (view.blurb ?? '') !== blurb.trim() ||
    view.audience !== audience ||
    view.mediaIds.join() !== mediaIds.join();

  const save = useMutation({
    mutationFn: () => wrapupApi.save(eventId, { blurb: blurb.trim() || null, mediaIds, audience }),
    onSuccess: (next) => {
      qc.setQueryData(['wrapup', eventId], next);
      void qc.invalidateQueries({ queryKey: ['wrapup-preview', eventId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not save the draft'),
  });
  const saveIfDirty = async () => {
    if (dirty) await save.mutateAsync();
  };

  const previewPersonId = previewAs?.personId ?? undefined;
  const preview = useQuery({
    queryKey: ['wrapup-preview', eventId, previewPersonId ?? 'me'],
    queryFn: () => wrapupApi.preview(eventId, previewPersonId),
  });

  const send = useMutation({
    mutationFn: async (test: boolean) => {
      await saveIfDirty();
      return wrapupApi.send(eventId, test);
    },
    onSuccess: (r) => {
      if (r.test) {
        toast.success(
          r.queued
            ? 'Test sent to you. Check your email.'
            : "The test reached nobody — you're not on this test event's roster.",
        );
        return;
      }
      toast.success(
        r.queued === 0 && r.alreadySent
          ? 'Everyone in this audience already has their copy.'
          : `Sent to ${r.queued} member${r.queued === 1 ? '' : 's'}${r.alreadySent ? ` (${r.alreadySent} already had theirs)` : ''}.`,
      );
      void qc.invalidateQueries({ queryKey: ['wrapup', eventId] });
      void qc.invalidateQueries({ queryKey: ['event-comms', eventId] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not send the wrap-up'),
  });

  const count = view.recipientCounts[audience];
  async function sendForReal() {
    const ok = await confirm({
      title: view.sentAt ? 'Send to anyone who hasn’t had it?' : `Send the wrap-up to ${count}?`,
      description: view.sentAt
        ? 'Members who already received the wrap-up are skipped; only people new to this audience get a copy.'
        : `${WRAPUP_AUDIENCE_LABELS[audience]} (${count}) will each get their own copy by email, with their scores beside the group's.`,
      confirmLabel: view.sentAt ? 'Send to the rest' : `Send to ${count}`,
    });
    if (ok) send.mutate(false);
  }

  function togglePhoto(mediaId: string) {
    setMediaIds((prev) =>
      prev.includes(mediaId)
        ? prev.filter((m) => m !== mediaId)
        : prev.length >= MAX_WRAPUP_PHOTOS
          ? prev
          : [...prev, mediaId],
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      <div className="min-w-0 space-y-5">
        {view.sentAt ? (
          <div className="rounded-lg border border-success/40 bg-success/5 p-3 text-sm text-cream-200">
            Sent {new Date(view.sentAt).toLocaleString()}
            {view.sentByName ? ` by ${view.sentByName}` : ''}. Sending again only reaches people who
            haven't had it.
          </div>
        ) : null}

        <section className="card space-y-4 p-5">
          <Textarea
            label="A few words about the night"
            hint="Leave blank to open with the standard “Tasting wrap-up” letter. {{firstName}} works here."
            className="min-h-[150px]"
            maxLength={5000}
            placeholder="Gentlemen, what an evening. The food at the club was superb…"
            value={blurb}
            onChange={(e) => setBlurb(e.target.value)}
          />

          <div>
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm text-cream-200">Photos</span>
              <span className="text-xs text-cream-300/50">
                {mediaIds.length}/{MAX_WRAPUP_PHOTOS} chosen · they appear in the order you pick
                them
              </span>
            </div>
            {view.photos.length === 0 ? (
              <p className="text-xs text-cream-300/50">
                No approved photos for this tasting yet. Add them under “Photos from the night” on
                the tasting page.
              </p>
            ) : (
              <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {view.photos.map((ph) => {
                  const order = mediaIds.indexOf(ph.mediaId);
                  const chosen = order >= 0;
                  return (
                    <li key={ph.mediaId}>
                      <button
                        type="button"
                        onClick={() => togglePhoto(ph.mediaId)}
                        aria-pressed={chosen}
                        aria-label={`${chosen ? 'Remove' : 'Use'} photo${ph.caption ? `: ${ph.caption}` : ''}`}
                        className={`relative block aspect-square w-full overflow-hidden rounded-md border-2 transition ${
                          chosen
                            ? 'border-gold-400'
                            : 'border-transparent opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={ph.url}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                        {chosen ? (
                          <span className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-gold-500 text-xs font-semibold text-on-gold">
                            {order + 1}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <fieldset>
            <legend className="mb-2 text-sm text-cream-200">Send to</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {WRAPUP_AUDIENCES.map((a) => (
                <label
                  key={a}
                  className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition ${
                    audience === a
                      ? 'border-gold-500/70 bg-gold-500/5'
                      : 'border-ink-800 hover:border-gold-600/40'
                  }`}
                >
                  <input
                    type="radio"
                    name="wrapup-audience"
                    className="mt-1 h-4 w-4 accent-gold-500"
                    checked={audience === a}
                    onChange={() => setAudience(a)}
                  />
                  <span>
                    <span className="block text-sm text-cream-100">
                      {WRAPUP_AUDIENCE_LABELS[a]}{' '}
                      <span className="text-gold-300">({view.recipientCounts[a]})</span>
                    </span>
                    <span className="block text-xs text-cream-300/55">{AUDIENCE_HINT[a]}</span>
                  </span>
                </label>
              ))}
            </div>
            {isTest ? (
              <p className="mt-2 text-xs text-warning">
                Test event: counts and sends are limited to the test roster.
              </p>
            ) : null}
          </fieldset>

          <div className="flex flex-wrap items-center gap-3 border-t border-ink-800 pt-4">
            <Button
              variant="ghost"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {dirty ? 'Save draft' : 'Draft saved'}
            </Button>
            <Button
              variant="quiet"
              loading={send.isPending && send.variables === true}
              onClick={() => send.mutate(true)}
            >
              Send a test to myself
            </Button>
            <Button
              className="sm:ml-auto"
              disabled={count === 0}
              loading={send.isPending && send.variables === false}
              onClick={() => void sendForReal()}
            >
              {view.sentAt ? 'Send to the rest' : `Send to ${count}`}
            </Button>
          </div>
        </section>

        <ResultsTable view={view} />
      </div>

      <section className="card min-w-0 space-y-3 p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-sm uppercase tracking-wide text-gold-400">Preview</h2>
            <p className="text-xs text-cream-300/55">
              {dirty
                ? 'Save the draft to see your latest changes here.'
                : 'Exactly what arrives, for one recipient.'}
            </p>
          </div>
          <div className="w-full sm:w-64">
            <span className="mb-1 block text-xs text-cream-300/60">Preview as</span>
            <PersonPicker
              value={previewAs}
              onChange={setPreviewAs}
              placeholder={
                user ? `${user.firstName} ${user.lastName} (you)` : 'Type a member’s name'
              }
            />
          </div>
        </div>
        {preview.data ? (
          <>
            <p className="truncate text-sm text-cream-100">
              <span className="text-cream-300/55">Subject:</span> {preview.data.subject}
              {previewAs?.personId ? null : (
                <>
                  {' '}
                  <Badge>you</Badge>
                </>
              )}
            </p>
            <EmailFrame html={preview.data.html} title={`Wrap-up as ${preview.data.personName}`} />
          </>
        ) : preview.isError ? (
          <p className="text-sm text-danger">Could not render the preview.</p>
        ) : (
          <p className="text-sm text-cream-300/50">Rendering…</p>
        )}
      </section>
    </div>
  );
}

/** Wide enough for the email's 560px card plus its margin. */
const EMAIL_WIDTH = 620;

/**
 * The email in a sandboxed frame (no scripts), sized to its content. Rendered at
 * the width a desktop mail client gives it and scaled down to fit a narrower
 * column, the way a phone's mail app shrinks it — never clipped.
 */
function EmailFrame({ html, title }: { html: string; title: string }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(900);
  const [width, setWidth] = useState(EMAIL_WIDTH);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry!.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const fit = () => {
    const doc = ref.current?.contentDocument;
    if (doc?.body) setHeight(Math.max(400, doc.documentElement.scrollHeight + 8));
  };
  const scale = Math.min(1, width / EMAIL_WIDTH);
  return (
    <div
      ref={box}
      className="overflow-hidden rounded-md border border-ink-800 bg-[#f5ecdd]"
      style={{ height: height * scale }}
    >
      <iframe
        ref={ref}
        title={title}
        srcDoc={html}
        sandbox="allow-same-origin"
        onLoad={fit}
        style={{
          width: scale < 1 ? EMAIL_WIDTH : '100%',
          height,
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: 'top left',
        }}
        className="block border-0"
      />
    </div>
  );
}

/** What the table in the email will say, for checking the facts before sending. */
function ResultsTable({ view }: { view: WrapupDraftView }) {
  const rows = useMemo(() => view.results, [view.results]);
  return (
    <section className="card p-5">
      <h2 className="mb-3 text-sm uppercase tracking-wide text-gold-400">The results</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-cream-300/50">No wines on this tasting.</p>
      ) : (
        <ul className="divide-y divide-ink-800/60">
          {rows.map((r) => (
            <li key={r.wineId} className="flex items-center gap-3 py-2">
              <span className="w-9 shrink-0 text-xs text-cream-300/60">
                {r.groupRank != null ? ordinal(r.groupRank) : '—'}
              </span>
              <span className="w-5 shrink-0 font-display text-lg text-gold-300">{r.letter}</span>
              {r.labelImageUrl ? (
                <img
                  src={r.labelImageUrl}
                  alt=""
                  className="h-10 w-8 shrink-0 rounded-sm object-cover"
                />
              ) : (
                <span className="h-10 w-8 shrink-0 rounded-sm bg-ink-800/60" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-cream-100">{r.display}</span>
                {r.broughtByName ? (
                  <span className="block truncate text-xs text-cream-300/55">
                    Brought by {r.broughtByName}
                  </span>
                ) : null}
              </span>
              <span className="shrink-0 text-right text-sm text-cream-100">
                {r.groupScore != null ? points(r.groupScore) : '—'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
